import { useState, type ReactNode } from 'react';
import { PersonFace } from '../person-face';
import {
  type LeaveRequestDetail,
  type OvertimeRequestDetail,
  type OvertimeSettlement,
} from '@forecastify/shared';
import { Button, Icon, TextInput } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useT, type Translate } from '../../i18n';
import { Attachments } from '../attachments';
import { COMMENT_PROSE, CommentComposer } from '../tasks/comment-composer';
import {
  DecisionControls,
  EMPTY_DECISION,
  settleBody,
  type DecisionDraft,
  type DecisionOptions,
} from './decision-controls';
import { CollapsibleHistory } from '../history/collapsible-history';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE REQUEST — the mockup's `leavereq`, and it serves BOTH kinds.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The first version was a two-column page of plain cards. The mockup draws a detail with a spine:
 *
 * | Left                                       | Right rail                                  |
 * | ------------------------------------------ | ------------------------------------------- |
 * | code · type badge, then the title           | status, then Elfogadás / Elutasítás          |
 * | requester · working days · decider          | the requester's quota, with the bar          |
 * | the range, and every day it costs           | details: type, frame year, requester, …      |
 * | note, attachments                           |                                             |
 * | activity — the thread                       |                                             |
 *
 * ⚠️⚠️ **AN OVERTIME CLAIM RENDERS THE SAME SPINE WITH DIFFERENT ORGANS, AND IT HAD NO SCREEN AT
 * ALL.** Its middle is one worklog rather than a range: the day, the task, the hours and the
 * multiplier that will freeze on approval (P3-5). It carries no thread and no attachment, because
 * `0113` hung those on `leave_requests` — the conversation about an hour belongs on the hour.
 *
 * ⚠️ **`wide` is the layout switch, not a second component.** Narrow (the drawer) stacks the rail
 * under the body; wide (the modal and the page) puts it beside. `task-detail.tsx` does the same and
 * for the same reason: two components would drift the moment one gained a field.
 */

export type RequestDetailData =
  | { readonly kind: 'leave'; readonly request: LeaveRequestDetail }
  | { readonly kind: 'overtime'; readonly claim: OvertimeRequestDetail };

export interface RequestDetailHandlers {
  /**
   * ⚠️⚠️ **THE SETTLEMENT RIDES WITH THE DECISION — feedback round 4 item 31.** Absent on a
   * leave request, which has no such choice, and absent on a rejection, which settles nothing.
   */
  readonly onDecide: (
    decision: 'approved' | 'rejected',
    note: string,
    settle?: {
      settlement: OvertimeSettlement;
      creditLeaveTypeId?: string;
      /** ⚠️ Which kind of overtime a PAYOUT is priced as — `0147`. Absent takes the day's. */
      overtimeTypeId?: string;
    },
  ) => void;
  /**
   * ⚠️⚠️ **WHAT AN OVERTIME DECISION CHOOSES FROM** — where a credit may land (item 31) and
   * what a payout may be priced as (`0147`). Both empty where the reader can decide nothing, and
   * then neither choice is drawn. `decision-options.ts` reads them; `decision-controls.tsx` draws
   * them, here and in the row menu's dialog.
   */
  readonly options?: DecisionOptions;
  readonly onRevoke: () => void;
  readonly onComment: (bodyHtml: string) => void;
  readonly onAttach: (file: File) => Promise<string | null>;
  readonly onOpenTask?: (key: string) => void;
}

/** The mockup's `LEAVE_ST` — one colour and one word per status, as the status tokens. */
export function statusColor(status: string): string {
  if (status === 'approved') return 'var(--status-ok)';
  if (status === 'rejected') return 'var(--status-bad)';
  if (status === 'revoked') return 'var(--status-idle)';
  return 'var(--status-warn)';
}

export function statusWord(t: Translate, status: string): string {
  /* ⚠️ One `t()` per branch with a literal key. */
  if (status === 'approved') return t('leave.status.approved');
  if (status === 'rejected') return t('leave.status.rejected');
  if (status === 'revoked') return t('leave.status.revoked');
  return t('leave.status.pending');
}

/**
 * ⚠️ **What a certificate may be.** The server decides for real (`sniff.ts` reads the magic number);
 * this is the picker's filter and the hint beside it, and the two must not disagree.
 */
const ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

/** ⚠️ The four codes C22 made distinct, each with its own sentence — rule 5. */
const ATTACH_ERROR: Readonly<Record<string, string>> = {
  file_too_large: 'task.attachment.tooLarge',
  file_infected: 'task.attachment.infected',
  scanner_unavailable: 'task.attachment.scannerDown',
  unsupported_file_type: 'task.attachment.failed',
};

export function RequestDetail({
  data,
  handlers,
  wide,
  serverError,
}: {
  readonly data: RequestDetailData;
  readonly handlers: RequestDetailHandlers;
  readonly wide: boolean;
  readonly serverError: string | null;
}) {
  const t = useT();
  const { fmt } = useDuration();
  /* ⚠️ Empty means the choice is not drawn, which is every reader who cannot decide anything. */
  const options = handlers.options ?? { creditTypes: [], overtimeTypes: [] };
  const [note, setNote] = useState('');
  /* ⚠️ `payout` and no named type is what approval has always meant, so it is what an untouched
     form sends — and `decide` defaults to the same, so the two cannot disagree. */
  const [decision, setDecision] = useState<DecisionDraft>(EMPTY_DECISION);
  const [attachFailure, setAttachFailure] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const common =
    data.kind === 'leave'
      ? {
          id: data.request.id,
          code: data.request.identifier,
          status: data.request.status,
          memberName: data.request.memberName,
          memberAvatarSeed: data.request.memberAvatarSeed,
          memberAvatarUrl: data.request.memberAvatarUrl,
          memberId: data.request.memberId,
          canDecide: data.request.canDecide,
          canRevoke: data.request.canRevoke,
          decidedByName: data.request.decidedByName,
          createdAt: data.request.createdAt,
          decidedAt: data.request.decidedAt,
          decisionNote: data.request.decisionNote,
        }
      : {
          id: data.claim.id,
          code: data.claim.identifier,
          status: data.claim.status,
          memberName: data.claim.memberName,
          memberAvatarSeed: data.claim.memberAvatarSeed,
          memberAvatarUrl: data.claim.memberAvatarUrl,
          memberId: data.claim.memberId,
          canDecide: data.claim.canDecide,
          /* The claimant, while it is pending — the server answers it, as it does for leave. */
          canRevoke: data.claim.canRevoke,
          decidedByName: data.claim.decidedByName,
          createdAt: data.claim.createdAt,
          decidedAt: data.claim.decidedAt,
          decisionNote: data.claim.decisionNote,
        };

  /* 27 Oct 2026 · who decided, with the face the row carries — an overtime claim's approver. */
  const decider =
    data.kind === 'leave'
      ? {
          name: data.request.decidedByName,
          seed: data.request.decidedByAvatarSeed,
          url: data.request.decidedByAvatarUrl,
        }
      : {
          name: data.claim.decidedByName ?? data.claim.approverName,
          seed: data.claim.approverAvatarSeed,
          url: data.claim.approverAvatarUrl,
        };

  /**
   * ⚠️⚠️ **THE DURATION IS THE TITLE — 15 Sep.** It was `type · dates`, and the type moved into the
   * header beside the code, so repeating it here was the duplication the report names. What a
   * reader wants from a leave request first is WHEN, and how long.
   */
  const title =
    data.kind === 'leave'
      ? `${data.request.fromDate} – ${data.request.toDate}`
      : data.claim.workedOn;

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THREE BLOCKS, BECAUSE THE NARROW LAYOUT WANTS THEM IN A THIRD ORDER. │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * Wide — the modal and the page — is two columns: everything on the left, the rail on the right.
   * Narrow — the drawer — stacks them, and stacking *body then rail* buried the status, the quota
   * and the details under the conversation. Reported on 15 Sep: those three are what somebody opens
   * a request to see, and a thread nobody has written in yet was standing in front of them.
   *
   * ⚠️ **So narrow reads: what it costs, then the rail, then the conversation.** The split is three
   * `ReactNode`s rather than three components — a component declared inside another gets a new
   * identity every render and remounts its whole subtree, which is the bug `app-shell.tsx` carries
   * the long note about.
   */
  /* ⚠️ The title and its meta line — the frame's pinned header since 14 Oct, see below. */
  const headline = (
    <div>
      {/* ⚠️ The code and the type live in the HEADER now — `request-surface.tsx`. Repeating
            them three centimetres below it is what the report calls duplication. */}
      <h2 className="text-[18px] font-semibold tracking-tight">{title}</h2>
      <div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-2 text-[12px]">
        <span className="flex items-center gap-1.5">
          {/* 28 Oct 2026 · their photo where they have one — `PersonFace`. */}
          <PersonFace
            name={common.memberName}
            seed={common.memberAvatarSeed}
            url={common.memberAvatarUrl}
            colorKey={common.memberId}
          />
          <span className="text-foreground">{common.memberName}</span>
        </span>
        {/* ⚠️ **The day count is gone from beside the person — 15 Sep.** It is stated in the
              breakdown card below, where the days it counts are, and a figure repeated beside a
              name reads as a property of the person. An overtime claim keeps its arithmetic,
              which is not a repeat of anything. */}
        {/**
         * ⚠️⚠️ **A CREDITED CLAIM SAYS WHAT IT BECAME, NOT WHAT IT WOULD HAVE COST** — feedback
         * round 4 item 31. The arithmetic is about money and a credit is not money; showing
         * *"3:00 × 1.5 = 4:30"* beside a claim that paid nothing is the one line most likely to
         * be read as a payment that happened.
         */}
        {data.kind === 'leave' ? null : data.claim.settlement === 'credit' ? (
          <span>
            {t('leave.settle.credited', {
              days: Number(data.claim.creditedDays ?? '0').toLocaleString(),
              type: data.claim.creditLeaveTypeName ?? '',
            })}
          </span>
        ) : data.claim.multiplier === null ? (
          /* ⚠️⚠️ **THE HOURS, AND NO ARITHMETIC — `0147`.** Until an approver says what kind of
               overtime this was there is no rate, so there is no product to show; the old fallback
               printed the TYPE's current one and read as a decision. */
          <span>{fmt(Number(data.claim.hours))}</span>
        ) : (
          <span>
            {fmt(Number(data.claim.hours))} × {Number(data.claim.multiplier).toLocaleString()} ={' '}
            {fmt(Number(data.claim.accountedHours ?? '0'))}
          </span>
        )}
        {common.decidedByName === null ? null : (
          <span>{t('leave.detail.decidedByName', { name: common.decidedByName })}</span>
        )}
      </div>
    </div>
  );

  const head = (
    <>
      {/* ── what it costs ──────────────────────────────────────────────────────────────── */}
      {data.kind === 'leave' ? (
        <section className="rounded-xl border">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Icon name="calendar-days" size={14} className="text-muted-foreground" />
            <span className="text-[12.5px] font-medium">
              {data.request.fromDate} – {data.request.toDate}
            </span>
            {/* ⚠️⚠️ **THE `7 nap` COUNT IS GONE — ASKED FOR TWICE.** Batch 3 took it off the
                line beside the person and left it here, which is not what was asked: the chips
                below ARE the count, one per working day, and a number over them is the same fact
                written twice in a smaller font. */}
          </div>
          {/* ⚠️⚠️ Every working day the range costs, with what it was worth — the only place the
              product shows WHY five calendar days cost three. */}
          <div className="flex flex-wrap gap-1.5 p-3">
            {data.request.dayBreakdown.map((day) => (
              <span
                key={day.date}
                className="bg-muted/50 rounded-md border px-2 py-1 text-[11.5px] tabular-nums"
              >
                {/* ⚠️ **The hours are gone — 15 Sep.** Every working day in a leave range is
                    worth the same full day, so the column read `8:00` on every chip: a figure
                    that never varies is a figure nobody reads. What the breakdown is FOR is
                    which days count, and that is the date. */}
                {day.date}
              </span>
            ))}
            {data.request.dayBreakdown.length === 0 ? (
              <span className="text-muted-foreground text-[12px]">
                {t('leave.detail.noWorkingDays')}
              </span>
            ) : null}
          </div>
        </section>
      ) : (
        <section className="rounded-xl border">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Icon name="clock" size={14} className="text-muted-foreground" />
            <span className="text-[12.5px] font-medium">{data.claim.workedOn}</span>
            <div className="flex-1" />
            <span className="text-muted-foreground text-[11.5px] tabular-nums">
              {fmt(Number(data.claim.hours))}
            </span>
          </div>
          <button
            type="button"
            className="hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors"
            onClick={() => {
              if (data.claim.taskKey !== null) handlers.onOpenTask?.(data.claim.taskKey);
            }}
          >
            <span className="text-muted-foreground shrink-0 text-[11.5px]">
              {data.claim.taskKey ?? '–'}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
              {data.claim.taskTitle ?? t('leave.detail.noTask')}
            </span>
            <span className="text-muted-foreground shrink-0 text-[11.5px]">
              {data.claim.projectName ?? t('projects.internal')}
            </span>
          </button>
          {data.claim.worklogNote === null ? null : (
            <p className="text-muted-foreground border-t px-3 py-2 text-[12px]">
              {data.claim.worklogNote}
            </p>
          )}
        </section>
      )}
    </>
  );

  const tail = (
    <>
      {/* ── note ───────────────────────────────────────────────────────────────────────── */}
      <section>
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {t('leave.field.note')}
        </div>
        <p className="mt-1 text-[12.5px] whitespace-pre-wrap">
          {(data.kind === 'leave' ? data.request.note : data.claim.note) ??
            t('leave.detail.noNote')}
        </p>
      </section>

      {/* ── attachments and the thread — leave only, and the schema says why ────────────── */}
      {data.kind === 'leave' ? (
        <>
          <section>
            {attachFailure === null ? null : (
              <p className="text-destructive mb-1 text-[11.5px]">
                {t(
                  (ATTACH_ERROR[attachFailure] ??
                    'task.attachment.failed') as 'task.attachment.failed',
                )}
              </p>
            )}

            {/**
             * ⚠️⚠️ **ITEM 12's ONE ROW, HERE TOO.** This section shipped as a button beside a
             * list; 11 Sep replaced that with a `DropZone`, because somebody holding the
             * certificate should not have to find it through a file tree. Feedback round 4 item 12
             * replaced the dashed box itself — on the TASK — and left this one behind. Both ways in
             * survive the change: the button is the discoverable one and the whole section is the
             * drop target, which is larger than the box was.
             *
             * ⚠️ **The *kötelező* note goes under the list rather than beside the heading**, which is
             * the only thing the shared heading cannot carry. It is a rule about the REQUEST, not
             * about the file list, and it reads as well there.
             */}
            <Attachments
              key={nonce}
              target="leave_request"
              targetId={data.request.id}
              canEdit
              heading={t('leave.detail.attachments')}
              variant="section"
              emptyNote={t('leave.detail.noAttachments')}
              accept={ATTACHMENT_TYPES}
              onUpload={async (files) => {
                setAttachFailure(null);
                for (const file of files) {
                  const code = await handlers.onAttach(file);
                  if (code !== null) {
                    setAttachFailure(code);
                    return;
                  }
                }
                setNonce((count) => count + 1);
              }}
            />

            {data.request.attachmentRule === 'required' ? (
              <p className="text-muted-foreground mt-1 text-[11px]">
                {t('leave.detail.attachmentRequired')}
              </p>
            ) : null}
          </section>

          <section>
            <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
              {t('leave.detail.thread')}
            </div>
            <div className="mt-1.5 rounded-xl border">
              {data.request.comments.length === 0 ? (
                <p className="text-muted-foreground px-3 py-3 text-[12.5px]">
                  {t('leave.detail.noComments')}
                </p>
              ) : (
                data.request.comments.map((one) => (
                  <div key={one.id} className="border-b px-3 py-2.5 last:border-0">
                    <div className="text-muted-foreground flex items-center gap-2 text-[11.5px]">
                      <PersonFace
                        name={one.authorName}
                        seed={one.authorAvatarSeed}
                        url={one.authorAvatarUrl}
                        colorKey={one.authorMemberId}
                      />
                      <span className="text-foreground font-medium">{one.authorName}</span>
                      <span>{one.createdAt.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    {/**
                     * ⚠️⚠️ **`dangerouslySetInnerHTML`, AND IT IS SAFE FOR ONE REASON:
                     * `leave-request.service.ts` SANITISES ON WRITE — `0119`.** The same
                     * allow-list a task comment goes through, called on the same pair of lines.
                     * If that ever weakens, this becomes stored XSS.
                     */}
                    <div
                      className={`mt-1 text-[12.5px] ${COMMENT_PROSE}`}
                      dangerouslySetInnerHTML={{ __html: one.bodyHtml }}
                    />
                  </div>
                ))
              )}
              {!data.request.canComment ? null : (
                /**
                 * ⚠️⚠️ **THE TASK THREAD'S COMPOSER, NOT A SECOND ONE — 15 Sep.** This was a
                 * single-line `TextInput` writing plain text, beside a task thread that has had a
                 * real editor since C19: a toolbar, links, lists, paste handling. Two comment
                 * boxes in one product that behave differently is a difference nobody can explain
                 * to the person typing in them, and the second one always drifts.
                 *
                 * ⚠️ **No `people` and no files.** A leave thread has no mention machinery on the
                 * server — nothing would be notified — and the request's documents are the
                 * `Attachments` section above, which is where a certificate belongs.
                 */
                <div className="border-t px-3 py-2.5">
                  <CommentComposer
                    allowFiles={false}
                    submitLabel={t('leave.detail.send')}
                    onSubmit={(html) => {
                      handlers.onComment(html);
                    }}
                  />
                </div>
              )}
            </div>
          </section>
        </>
      ) : null}

      {/* ── history — ux-pass-2: both kinds, the same list as every other record ──────────── */}
      {data.kind === 'leave' ? (
        <CollapsibleHistory type="leave_request" id={data.request.id} />
      ) : (
        <CollapsibleHistory type="overtime_request" id={data.claim.id} />
      )}
    </>
  );

  const rail = (
    <div className="flex flex-col gap-3">
      <section className="rounded-xl border p-3">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {t('leave.column.status')}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <span
            className="size-2 rounded-full"
            style={{ background: statusColor(common.status) }}
          />
          <span className="text-[13px] font-medium">{statusWord(t, common.status)}</span>
        </div>

        {common.status !== 'pending' || !common.canDecide ? null : (
          <>
            {/**
             * ⚠️⚠️ **HOW AN APPROVED CLAIM SETTLES, AND WHAT IT IS PRICED AS.** Item 31 and
             * `0147` — the two decisions that are the approver's and nobody else's. Leave has
             * neither, so the rail is the note and the two buttons for a leave request.
             *
             * ⚠️ **The controls live in `decision-controls.tsx`** because the row menu's dialog
             * takes exactly the same decision; that file records why they are not written twice.
             */}
            {data.kind !== 'overtime' ? null : (
              <div className="mt-2.5">
                <DecisionControls
                  draft={decision}
                  options={options}
                  onChange={setDecision}
                  compact
                />
              </div>
            )}

            <TextInput
              value={note}
              placeholder={t('leave.detail.decisionNote')}
              aria-label={t('leave.detail.decisionNote')}
              className="mt-2.5 h-8 w-full text-[12.5px]"
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => {
                  handlers.onDecide(
                    'approved',
                    note,
                    /* ⚠️ Only an overtime claim settles — see the control above. One
                       assembler for both surfaces: `settleBody` omits rather than empties, so an
                       absent field means *take the default*. */
                    data.kind === 'overtime' ? settleBody(decision) : undefined,
                  );
                }}
              >
                <Icon name="circle-check" size={14} />
                {t('leave.action.approve')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => {
                  handlers.onDecide('rejected', note);
                }}
              >
                <Icon name="x" size={14} />
                {t('leave.action.reject')}
              </Button>
            </div>
          </>
        )}

        {common.decisionNote === null ? null : (
          <p className="text-muted-foreground mt-2 text-[12px] whitespace-pre-wrap">
            {common.decisionNote}
          </p>
        )}

        {/* ⚠️ Revoking is the REQUESTER's act and works while pending OR approved. */}
        {!common.canRevoke ? null : (
          <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={handlers.onRevoke}>
            {t('leave.action.revoke')}
          </Button>
        )}

        {serverError === null ? null : (
          <p className="text-destructive mt-2 text-[12px]">{serverError}</p>
        )}
      </section>

      {/* ── the quota this request moves ───────────────────────────────────────────────── */}
      {data.kind === 'leave' && data.request.balance !== null ? (
        <QuotaCard balance={data.request.balance} />
      ) : null}

      <section className="rounded-xl border p-3">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {t('leave.detail.details')}
        </div>
        <dl className="mt-2 flex flex-col gap-1.5 text-[12.5px]">
          <Row label={t('leave.column.type')}>
            {/* ⚠️ An em-dash on a claim nobody has priced yet — `0147`. */}
            {data.kind === 'leave'
              ? data.request.leaveTypeName
              : (data.claim.overtimeTypeName ?? '–')}
          </Row>
          {data.kind === 'leave' ? (
            <Row label={t('leave.column.year')}>{String(data.request.year)}</Row>
          ) : null}
          <Row label={t('dash.column.person')}>{common.memberName}</Row>
          <Row label={t('leave.column.decidedBy')}>
            {decider.name === null ? (
              '–'
            ) : (
              /* 27 Oct 2026 · with their face, as everywhere else a person is named.
                 ⚠️ 28 Oct · 16px, so the row keeps the text rows' height, and the NAME is what
                 shortens — the face never wraps away from it. */
              <span className="inline-flex max-w-full items-center gap-1.5 align-middle">
                <PersonFace name={decider.name} seed={decider.seed} url={decider.url} size={16} />
                <span className="min-w-0 truncate">{decider.name}</span>
              </span>
            )}
          </Row>
          <Row label={t('leave.column.created')}>{common.createdAt.slice(0, 10)}</Row>
          <Row label={t('leave.detail.decidedAt')}>
            {common.decidedAt === null ? '–' : common.decidedAt.slice(0, 10)}
          </Row>
        </dl>
      </section>
    </div>
  );

  /**
   * ⚠️⚠️ **THE TITLE STAYS, AND THE RAIL SCROLLS ON ITS OWN — 14 Oct.** The product owner's two
   * sentences: the header *"always visible when scrolling"*, and *"scrolling the main part leaves
   * the sidebar intact"*. `DetailFrame` in `contracts/detail-chrome.tsx` is the same shape and
   * carries the argument for every class; this one keeps its own `p-4` rhythm and its `xl`
   * breakpoint, because a request's rail is 320px and wider than the contracts'.
   *
   * ⚠️ **Opaque and ruled**, because content passes beneath it, and `sticky` in the drawer, where
   * the `Sheet` owns the one scroll region.
   */
  const pinned = (
    <div className="bg-background sticky top-0 z-10 shrink-0 border-b px-4 pt-4 pb-3">
      {headline}
    </div>
  );

  if (!wide) {
    return (
      <div className="flex flex-col">
        {pinned}
        <div className="flex flex-col gap-4 p-4">
          {head}
          {rail}
          {tail}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {pinned}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 xl:flex-row xl:gap-0 xl:overflow-visible xl:p-0">
        <div className="flex min-w-0 flex-col gap-4 xl:flex-1 xl:overflow-y-auto xl:py-4 xl:pr-2 xl:pl-4">
          {head}
          {tail}
        </div>
        <div className="xl:w-[320px] xl:shrink-0 xl:overflow-y-auto xl:py-4 xl:pr-4 xl:pl-2">
          {rail}
        </div>
      </div>
    </div>
  );
}

function QuotaCard({
  balance,
}: {
  readonly balance: { quotaDays: number; usedDays: number; pendingDays: number };
}) {
  const t = useT();
  const available = Math.max(0, balance.quotaDays - balance.usedDays - balance.pendingDays);
  const pct = (value: number): number =>
    balance.quotaDays === 0
      ? 0
      : Math.max(0, Math.min(100, Math.round((value / balance.quotaDays) * 100)));

  return (
    <section className="rounded-xl border p-3">
      <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {t('leave.detail.quota')}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-[19px] font-medium tabular-nums">{available}</span>
        <span className="text-muted-foreground text-[12px] tabular-nums">
          / {balance.quotaDays} {t('home.quota.days')}
        </span>
      </div>
      <div className="bg-muted mt-2 flex h-2 overflow-hidden rounded-full">
        <span className="bg-primary h-full" style={{ width: `${String(pct(available))}%` }} />
        <span
          className="h-full"
          style={{
            width: `${String(pct(balance.pendingDays))}%`,
            background: 'var(--status-warn)',
          }}
        />
      </div>
      <dl className="text-muted-foreground mt-2 flex flex-col gap-1 text-[11.5px]">
        <div className="flex items-center justify-between">
          <dt>{t('leave.detail.used')}</dt>
          <dd className="tabular-nums">{balance.usedDays}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt>{t('leave.status.pending')}</dt>
          <dd className="tabular-nums">{balance.pendingDays}</dd>
        </div>
      </dl>
    </section>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    /* ⚠️ 28 Oct 2026 · centred, not baseline: a value with a face in it put its baseline on the
       circle, and the label sat off the name beside it. */
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}
